'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Navigation from '@/components/Navigation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getStoredAccessToken, normalizeRole, readStoredUser } from '@/lib/auth/session';
import { rentalsApi, type RentalBooking, type RentalBookingStatus } from '@/lib/api/rentals';
import { useCountry } from '@/context/CountryContext';
import { CalendarDays, CarFront, Clock3, RefreshCw, WalletCards } from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  pending: 'قيد المراجعة',
  confirmed: 'مؤكد',
  active: 'نشط',
  completed: 'مكتمل',
  cancelled: 'ملغي',
  rejected: 'مرفوض',
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  active: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-700',
  rejected: 'bg-red-100 text-red-700',
};

const NEXT_STATUSES: Record<string, RentalBookingStatus[]> = {
  pending: ['confirmed', 'rejected', 'cancelled'],
  confirmed: ['active', 'cancelled'],
  active: ['completed', 'cancelled'],
  completed: [],
  rejected: [],
  cancelled: [],
};

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'نقداً',
  card: 'بطاقة',
  bank: 'تحويل بنكي',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'معلق',
  partial: 'جزئي',
  paid: 'مدفوع',
  refunded: 'مسترد',
};

export default function RentalBookingsPage() {
  const router = useRouter();
  const { toast } = useToast();
  const { selectedCountry, selectedCountryCode } = useCountry();
  const currencyLabel = selectedCountry?.currency || selectedCountry?.currency_symbol || (selectedCountryCode === 'OM' ? 'ر.ع' : 'ر.س');
  const token = getStoredAccessToken();
  const sessionUser = readStoredUser();
  const role = normalizeRole(sessionUser?.role as string | undefined);
  const isVendorOrAdmin = role === 'vendor' || role === 'admin' || role === 'superadmin';

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<RentalBooking[]>([]);
  const [statusUpdate, setStatusUpdate] = useState<Record<number, RentalBookingStatus | ''>>({});
  const [extendDate, setExtendDate] = useState<Record<number, string>>({});
  const [workingId, setWorkingId] = useState<number | null>(null);

  const openBookings = useMemo(
    () => bookings.filter((b) => ['pending', 'confirmed', 'active'].includes(b.status)),
    [bookings]
  );

  const fetchBookings = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await rentalsApi.getMyBookings();
      const rows = Array.isArray((res as any).results) ? (res as any).results : (Array.isArray(res) ? res : []);
      setBookings(rows as RentalBooking[]);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleStatusUpdate = async (booking: RentalBooking) => {
    const next = statusUpdate[booking.id];
    if (!next) return;
    setWorkingId(booking.id);
    try {
      await rentalsApi.setBookingStatus(booking.id, { status: next });
      toast({ title: 'تم تحديث الحالة', description: `تم تغيير الحالة إلى ${STATUS_LABELS[next]}` });
      setStatusUpdate((p) => ({ ...p, [booking.id]: '' }));
      fetchBookings();
    } catch (error: any) {
      toast({ title: 'تعذر تحديث الحالة', description: error?.message || 'حاول مرة أخرى.' });
    } finally {
      setWorkingId(null);
    }
  };

  const handleExtend = async (booking: RentalBooking) => {
    const nextDate = extendDate[booking.id];
    if (!nextDate) return;
    setWorkingId(booking.id);
    try {
      await rentalsApi.extendBooking(booking.id, { end_date: nextDate });
      toast({ title: 'تم تمديد الحجز', description: 'تم تحديث تاريخ نهاية الحجز بنجاح.' });
      setExtendDate((p) => ({ ...p, [booking.id]: '' }));
      fetchBookings();
    } catch (error: any) {
      toast({ title: 'تعذر تمديد الحجز', description: error?.message || 'تحقق من التاريخ الجديد.' });
    } finally {
      setWorkingId(null);
    }
  };

  const handleCancel = async (booking: RentalBooking) => {
    setWorkingId(booking.id);
    try {
      await rentalsApi.cancelBooking(booking.id);
      toast({ title: 'تم إلغاء الحجز', description: `تم إلغاء ${booking.booking_number}` });
      fetchBookings();
    } catch (error: any) {
      toast({ title: 'تعذر الإلغاء', description: error?.message || 'غير مسموح بإلغاء هذا الحجز حالياً.' });
    } finally {
      setWorkingId(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation title="حجوزات الإيجار" />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Card className="rounded-3xl border-none shadow-sm">
            <CardContent className="py-16 text-center">
              <CarFront className="h-14 w-14 text-slate-300 mx-auto mb-3" />
              <p className="font-bold text-slate-700">يرجى تسجيل الدخول لعرض الحجوزات</p>
              <div className="flex justify-center gap-2 mt-4">
                <Button className="rounded-xl" onClick={() => router.push('/login')}>تسجيل الدخول</Button>
                <Link href="/rentals"><Button variant="outline" className="rounded-xl">العودة للتأجير</Button></Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation title="حجوزات الإيجار" />
      <div className="max-w-5xl mx-auto px-4 pb-24 pt-6 space-y-4">
        <Card className="rounded-3xl border-none shadow-sm">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-black text-slate-800">متابعة حجوزات الإيجار</h1>
              <p className="text-sm text-slate-500 mt-1">
                {isVendorOrAdmin
                  ? `الحجوزات المفتوحة الآن: ${openBookings.length}`
                  : 'يمكنك متابعة حالة طلبات الإيجار وتحديثاتها هنا.'}
              </p>
            </div>
            <Button variant="outline" className="rounded-xl h-9" onClick={fetchBookings}>
              <RefreshCw className="h-4 w-4 ml-1" /> تحديث
            </Button>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16"><div className="w-9 h-9 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : bookings.length === 0 ? (
          <Card className="rounded-3xl border-none shadow-sm">
            <CardContent className="py-16 text-center">
              <Clock3 className="h-14 w-14 text-slate-300 mx-auto mb-3" />
              <p className="font-bold text-slate-700">لا توجد حجوزات حتى الآن</p>
              <Link href="/rentals" className="inline-block mt-4"><Button className="rounded-xl">استعراض سيارات الإيجار</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {bookings.map((booking) => {
              const canCancel = ['pending', 'confirmed'].includes(booking.status);
              const nextStatuses = NEXT_STATUSES[booking.status] || [];
              return (
                <Card key={booking.id} className="rounded-3xl border-none shadow-sm">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-black text-slate-800">{booking.rental_car_title || 'سيارة إيجار'}</h3>
                        <p className="text-xs text-slate-500">{booking.booking_number} • {booking.vendor_store_name || 'مكتب التأجير'}</p>
                      </div>
                      <Badge className={STATUS_BADGES[booking.status] || 'bg-slate-100 text-slate-700'}>
                        {STATUS_LABELS[booking.status] || booking.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div className="rounded-xl bg-slate-50 p-3 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-slate-500" />
                        <span>{booking.start_date} → {booking.end_date}</span>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">الأيام: <span className="font-bold">{booking.total_days}</span></div>
                      <div className="rounded-xl bg-slate-50 p-3">الإجمالي: <span className="font-black text-blue-600">{booking.total_amount} {currencyLabel}</span></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div className="rounded-xl bg-blue-50 p-3 flex items-center gap-2 text-blue-800">
                        <WalletCards className="h-4 w-4" />
                        <span>الدفع: <span className="font-bold">{PAYMENT_LABELS[booking.payment_method || 'cash'] || booking.payment_method || '—'}</span></span>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">المقدم المطلوب: <span className="font-bold">{booking.advance_amount || 0} {currencyLabel}</span></div>
                      <div className="rounded-xl bg-slate-50 p-3">حالة الدفع: <span className="font-bold">{PAYMENT_STATUS_LABELS[booking.payment_status || 'pending'] || booking.payment_status}</span></div>
                    </div>

                    {(booking.notes || booking.vendor_notes) && (
                      <div className="text-xs text-slate-600 rounded-xl border border-slate-100 p-3 space-y-1">
                        {booking.notes && <p><span className="font-bold">ملاحظتك:</span> {booking.notes}</p>}
                        {booking.vendor_notes && <p><span className="font-bold">ملاحظات المزود:</span> {booking.vendor_notes}</p>}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {isVendorOrAdmin && nextStatuses.length > 0 && (
                        <>
                          <Select
                            value={statusUpdate[booking.id] || '__none'}
                            onValueChange={(v) => setStatusUpdate((p) => ({ ...p, [booking.id]: v === '__none' ? '' : (v as RentalBookingStatus) }))}
                          >
                            <SelectTrigger className="w-48 rounded-xl h-9">
                              <SelectValue placeholder="تغيير الحالة" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">اختر الحالة</SelectItem>
                              {nextStatuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button
                            className="rounded-xl h-9"
                            disabled={!statusUpdate[booking.id] || workingId === booking.id}
                            onClick={() => handleStatusUpdate(booking)}
                          >
                            حفظ الحالة
                          </Button>
                        </>
                      )}

                      {isVendorOrAdmin && ['pending', 'confirmed', 'active'].includes(booking.status) && (
                        <>
                          <Input
                            type="date"
                            className="w-44 rounded-xl h-9"
                            value={extendDate[booking.id] || ''}
                            onChange={(e) => setExtendDate((p) => ({ ...p, [booking.id]: e.target.value }))}
                          />
                          <Button
                            variant="outline"
                            className="rounded-xl h-9"
                            disabled={!extendDate[booking.id] || workingId === booking.id}
                            onClick={() => handleExtend(booking)}
                          >
                            تمديد
                          </Button>
                        </>
                      )}

                      {!isVendorOrAdmin && canCancel && (
                        <Button
                          variant="outline"
                          className="rounded-xl h-9 text-red-600 border-red-200 hover:bg-red-50"
                          disabled={workingId === booking.id}
                          onClick={() => handleCancel(booking)}
                        >
                          إلغاء الحجز
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
